import {ColumnDef} from "@tanstack/react-table";
import {sortableColumnHeader} from "@/components/data-table.tsx";
import {Link} from "@/components/tailwind/link.tsx";
import {FilePenLineIcon} from "lucide-react";
import {OrderOverviewItem, OrderOverviewMember} from "@/models/order.ts";
import {Text} from "@/components/tailwind/text.tsx";
import {formatDateString, formatDateTimeString} from "@/lib/utils.ts";

function generateNameColumnContent(row: OrderOverviewMember) {
    if (!row) return

    return (
        <span>
            <Text>{[row.salutation, row.title, row.firstName, row.lastName].filter(Boolean).join(" ")}</Text>
        </span>
    )
}

const baseColumns: ColumnDef<OrderOverviewItem>[] = [
    {
        accessorKey: "orderNo",
        header: ({column}) =>
            sortableColumnHeader(column, "orderList.table.headers.orderNo", "orders")
    },
    {
        accessorKey: "orderDate",
        header: ({column}) =>
            sortableColumnHeader(column, "orderList.table.headers.date", "orders"),
        cell: ({row}) => {
            return (
                <Text>
                    {formatDateString(row.original.orderDate)}
                </Text>
            )
        }
    },
    {
        id: "client",
        header: ({column}) =>
            sortableColumnHeader(column, "orderList.table.headers.member", "orders"),
        cell: ({row}) => generateNameColumnContent(row.original.client)
    },
    {
        accessorKey: "service",
        header: ({column}) =>
            sortableColumnHeader(column, "orderList.table.headers.service", "orders")
    },
    {
        id: "helper",
        header: ({column}) =>
            sortableColumnHeader(column, "orderList.table.headers.helper", "orders"),
        cell: ({row}) => generateNameColumnContent(row.original.helper)
    },
    {
        accessorKey: "comments",
        header: ({column}) =>
            sortableColumnHeader(column, "orderList.table.headers.comments", "orders")
    }
];

const actionsColumn: ColumnDef<OrderOverviewItem> = {
    id: "actions",
    cell: ({row}) => {
        const order = row.original

        return (
            <Link href={`/orders/${order.id}`} data-testid="order-edit-action"><FilePenLineIcon/></Link>
        )
    }
};

export const orderColumns = (t: any): ColumnDef<OrderOverviewItem>[] => [
    ...baseColumns,
    {
        accessorKey: "status",
        header: ({column}) =>
            sortableColumnHeader(column, "orderList.table.headers.status", "orders"),
        cell: ({row}) => {
            return t(`orderList.table.statusColumn.${row.original.status.toLowerCase()}`)
        }
    },
    actionsColumn
];

export const orderColumnsCompleted = (): ColumnDef<OrderOverviewItem>[] => [
    ...baseColumns,
    {
        accessorKey: "completedAt",
        header: ({column}) =>
            sortableColumnHeader(column, "orderList.table.headers.completedAt", "orders"),
        cell: ({row}) => {
            return (
                <Text>
                    {row.original.completedAt ? formatDateTimeString(row.original.completedAt) : '-'}
                </Text>
            )
        }
    },
    actionsColumn
];

export const orderColumnsCancelled = (): ColumnDef<OrderOverviewItem>[] => [
    ...baseColumns,
    {
        accessorKey: "cancelledAt",
        header: ({column}) =>
            sortableColumnHeader(column, "orderList.table.headers.cancelledAt", "orders"),
        cell: ({row}) => {
            return (
                <Text>
                    {row.original.cancelledAt ? formatDateTimeString(row.original.cancelledAt) : '-'}
                </Text>
            )
        }
    },
    actionsColumn
];
